<template>
  <el-dialog :model-value="modelValue" title="资产库" class="video-asset-library" width="1560px" top="6vh"
    :show-close="!uploadBusy && !deleteBusy" :close-on-click-modal="!uploadBusy && !deleteBusy && !previewItem" :close-on-press-escape="!uploadBusy && !deleteBusy && !previewItem"
    append-to-body destroy-on-close @update:model-value="emit('update:modelValue', $event)" @open="resetSelection">
    <div class="library-layout">
      <section class="library-browser" aria-label="可选资产">
        <div class="library-browser-heading"><span>全部资产</span><button v-if="canUpload" type="button" class="library-upload-trigger" :disabled="!scriptId || uploadBusy || deleteBusy" @click="uploadVisible = true"><el-icon><Upload /></el-icon>上传资产</button></div>
        <div class="library-toolbar">
          <label class="library-search"><el-icon><Search /></el-icon>
            <input v-model="keyword" type="search" placeholder="搜索名称或素材 ID" aria-label="搜索资产" />
          </label>
          <div class="library-type-filters" aria-label="素材类型">
            <button v-for="type in filters" :key="type.value" type="button" :class="{ active: activeType === type.value }"
              :aria-pressed="activeType === type.value" @click="activeType = type.value">{{ type.label }}<span>{{ typeCounts[type.value] }}</span></button>
          </div>
        </div>
        <div v-if="error" class="library-load-error" role="alert">{{ error }}<button type="button" class="library-text-button" @click="emit('reload')">重新加载</button></div>
        <div ref="gridRef" v-loading="loading" class="library-grid-scroll library-scroll" aria-label="资产列表" @scroll.passive="onGridScroll">
          <div v-if="filteredItems.length" class="library-grid-space" :style="{ height: `${gridWindow.height}px` }">
            <div class="library-asset-grid" :style="{ top: `${gridWindow.offset}px` }">
              <AssetLibraryCard v-for="item in visibleItems" :key="assetSelectionKey(item)" :item="item"
                :selected="selectedKeySet.has(assetSelectionKey(item))" :disabled-reason="selectionReason(item)"
                :can-delete="canDelete && !uploadBusy" :deleting="deletingKeys.has(assetSelectionKey(item))"
                @toggle="toggleItem" @preview="openPreview" @hover-start="scheduleHover" @hover-end="scheduleHide" @delete="removeAsset" />
            </div>
          </div>
          <div v-else-if="!loading" class="library-empty">
            <el-icon><Files /></el-icon>
            <strong>{{ keyword || activeType !== 'all' ? '未找到匹配的素材' : '暂无可用素材' }}</strong>
            <span>{{ keyword || activeType !== 'all' ? '试试其他关键词或素材类型' : '这里展示与 @ 列表相同的可用资产' }}</span>
          </div>
        </div>
        <div class="library-results">共 {{ filteredItems.length }} 项</div>
      </section>
      <aside class="library-selection" aria-label="已选素材">
        <div class="library-section-heading">
          <strong>已选参考 <span>{{ selectedItems.length }}</span></strong>
          <button type="button" class="library-text-button" :disabled="!selectedItems.length" @click="selectedKeys = []">清空</button>
        </div>
        <div class="library-limits" aria-label="参考数量">
          <span v-for="type in mediaTypes" :key="type.value" :class="type.value">
            {{ type.label }} <b>{{ pendingCounts[type.value] || 0 }} / {{ limits[type.category] }}</b>
          </span>
        </div>
        <div class="library-selected-scroll library-scroll" @scroll.passive="closeHover">
          <div v-if="selectedItems.length" class="library-selected-strip">
            <AssetLibraryCard v-for="item in selectedItems" :key="assetSelectionKey(item)" :item="item" selected compact :can-delete="canDelete && !uploadBusy" :deleting="deletingKeys.has(assetSelectionKey(item))"
              @toggle="toggleItem" @preview="openPreview" @hover-start="scheduleHover" @hover-end="scheduleHide" @delete="removeAsset" />
          </div>
          <div v-else class="library-selection-empty"><el-icon><Files /></el-icon><span>暂未选择参考</span><small>从左侧选择素材</small></div>
        </div>
        <p class="library-selection-note">已同步当前参考，确认后替换原队列</p>
      </aside>
    </div>
    <template #footer>
      <div class="library-footer">
        <span role="status">{{ selectionError || '选择素材后可在提示词中通过 @ 引用' }}</span>
        <div><el-button :disabled="uploadBusy || deleteBusy" @click="emit('update:modelValue', false)">取消</el-button>
          <el-button type="primary" class="library-confirm" :disabled="!!selectionError || loading || uploadBusy || deleteBusy" @click="emit('add', selectedItems)">
            {{ selectedItems.length ? `加入参考 (${selectedItems.length})` : '清空参考' }}
          </el-button></div>
      </div>
    </template>
  </el-dialog>
  <AssetLibraryHoverPreview v-if="modelValue" :item="hoverItem" :anchor="hoverAnchor" @keep="keepHover" @leave="scheduleHide" @preview="openPreview" />
  <AssetLibraryPreview v-if="modelValue && previewItem" :item="previewItem" @close="previewItem = null" />
  <AssetLibraryUploadDialog v-model="uploadVisible" :script-id="scriptId" @busy="uploadBusy = $event" />
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { Files, Search, Upload } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { deleteAsset } from '@/api/volcAsset'
import AssetLibraryHoverPreview from './AssetLibraryHoverPreview.vue'
import { hasPermission } from '@/api/auth'
import AssetLibraryCard from './AssetLibraryCard.vue'
import AssetLibraryPreview from './AssetLibraryPreview.vue'
import AssetLibraryUploadDialog from './AssetLibraryUploadDialog.vue'
import { assetDisplayName, assetMediaType, assetSelectionKey, assetSelectionReason } from '../model/assetLibrarySelection'

const props = defineProps({ modelValue: Boolean, items: { type: Array, default: () => [] },
  scriptId: { type: [String, Number], default: null },
  limits: { type: Object, required: true }, isWan: Boolean, loading: Boolean, error: { type: String, default: '' } })
const emit = defineEmits(['update:modelValue', 'add', 'reload', 'deleted'])
const keyword = ref(''), activeType = ref('all'), selectedKeys = ref([]), gridRef = ref(null)
const previewItem = ref(null), uploadVisible = ref(false), uploadBusy = ref(false)
const canUpload = computed(() => hasPermission('volc_asset:upload') && hasPermission('file:upload'))
const canDelete = computed(() => hasPermission('volc_asset:delete'))
const deletingKeys = ref(new Set())
const deleteBusy = computed(() => deletingKeys.value.size > 0)
const selectedKeySet = computed(() => new Set(selectedKeys.value))
const hoverItem = shallowRef(null), hoverAnchor = shallowRef(null)
let showTimer, hideTimer, searchTimer, scrollFrame, scrollIdleTimer
const searchTerm = ref('')
function keepHover() { clearTimeout(hideTimer); hideTimer = null }
function closeHover() {
  clearTimeout(showTimer); clearTimeout(hideTimer)
  showTimer = null; hideTimer = null
  hoverItem.value = null; hoverAnchor.value = null
}
function scheduleHover({ item, anchor }) {
  closeHover()
  if (previewItem.value || deleteBusy.value) return
  showTimer = setTimeout(() => {
    if (!props.modelValue || scrollIdleTimer || !anchor.isConnected) return
    hoverAnchor.value = anchor; hoverItem.value = item
  }, 240)
}
function scheduleHide() {
  clearTimeout(showTimer)
  if (hideTimer) return
  hideTimer = setTimeout(closeHover, 120)
}
function checkPointer(event) {
  if (!hoverItem.value) return
  if (hoverAnchor.value?.contains(event.target) || event.target?.closest?.('.video-library-preview')) keepHover()
  else scheduleHide()
}
function leaveWindow(event) { if (!event.relatedTarget) closeHover() }
function openPreview(item) { closeHover(); previewItem.value = item }
async function removeAsset(item) {
  const key = assetSelectionKey(item)
  if (!canDelete.value || !item.tableId || deletingKeys.value.has(key) || uploadBusy.value) return
  closeHover()
  deletingKeys.value = new Set([...deletingKeys.value, key])
  try {
    await ElMessageBox.confirm(`确定删除“${assetDisplayName(item)}”？删除后不可恢复。`, '删除确认', {
      confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning', customClass: 'video-library-delete-confirm'
    })
    const result = await deleteAsset(item.tableId)
    if (result.code !== 200) throw new Error(result.message || '删除失败')
    if (previewItem.value && assetSelectionKey(previewItem.value) === key) previewItem.value = null
    selectedKeys.value = selectedKeys.value.filter(value => value !== key)
    emit('deleted', item)
    ElMessage.success('删除成功')
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') ElMessage.error(error.message || '删除失败，请重试')
  } finally {
    deletingKeys.value = new Set([...deletingKeys.value].filter(value => value !== key))
  }
}
const gridSize = shallowRef({ width: 840, height: 370, columns: 6, gap: 12 })
const scrollRow = ref(0)
let resizeObserver
const mediaTypes = [{ value: 'image', label: '图片', category: 'images' }, { value: 'video', label: '视频', category: 'videos' }, { value: 'audio', label: '音频', category: 'audios' }]
const filters = [{ value: 'all', label: '全部' }, ...mediaTypes]
const itemMap = computed(() => new Map(props.items.map(item => [assetSelectionKey(item), item])))
const selectedItems = computed(() => selectedKeys.value.map(key => itemMap.value.get(key)).filter(Boolean))
const pendingCounts = computed(() => selectedItems.value.reduce((counts, item) => {
  const type = assetMediaType(item); counts[type] = (counts[type] || 0) + 1; return counts
}, {}))
// Normalize searchable fields once per asset update, not on every keystroke.
const searchIndex = computed(() => props.items.map(item => ({ item, type: assetMediaType(item),
  text: [assetDisplayName(item), item.uuid, item.assetId, item.instructionValue].map(value => String(value || '').toLowerCase()).join('\n') })))
const searchResult = computed(() => {
  const counts = { all: 0, image: 0, video: 0, audio: 0 }, items = []
  for (const entry of searchIndex.value) {
    if (searchTerm.value && !entry.text.includes(searchTerm.value)) continue
    counts.all++; counts[entry.type]++
    if (activeType.value === 'all' || entry.type === activeType.value) items.push(entry.item)
  }
  return { counts, items }
})
const typeCounts = computed(() => searchResult.value.counts)
const filteredItems = computed(() => searchResult.value.items)
const rowStride = computed(() => {
  const { width, columns, gap } = gridSize.value
  const cardWidth = (width - gap * (columns - 1)) / columns
  // Card: two borders + horizontal padding, 4:3 cover, name and vertical gaps.
  return Math.max(1, (cardWidth - 14) * .75 + 42 + gap)
})
const gridWindow = computed(() => {
  const { height, columns, gap } = gridSize.value
  const stride = rowStride.value
  const rows = Math.ceil(filteredItems.value.length / columns)
  const row = Math.max(0, Math.min(rows - 1, scrollRow.value))
  const startRow = Math.max(0, row - 1)
  // Cover a partially scrolled row plus one overscan row without tracking pixels.
  const endRow = Math.min(rows, row + Math.ceil(height / stride) + 2)
  return { start: startRow * columns, end: endRow * columns, offset: startRow * stride, height: Math.max(0, rows * stride - gap) }
})
const visibleItems = computed(() => filteredItems.value.slice(gridWindow.value.start, gridWindow.value.end))
function measureGrid() {
  const el = gridRef.value
  if (!el) return
  const style = getComputedStyle(el)
  const size = { width: el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
    height: el.clientHeight, columns: Number(style.getPropertyValue('--library-columns')) || 6,
    gap: Number(style.getPropertyValue('--library-gap')) || 12 }
  if (Object.keys(size).some(key => size[key] !== gridSize.value[key])) gridSize.value = size
  updateScrollRow()
}
function updateScrollRow() {
  if (gridRef.value) scrollRow.value = Math.max(0, Math.floor(gridRef.value.scrollTop / rowStride.value))
}
function clearScrollActivity() {
  clearTimeout(scrollIdleTimer); scrollIdleTimer = null
  cancelAnimationFrame(scrollFrame); scrollFrame = null
}
function onGridScroll() {
  closeHover()
  clearTimeout(scrollIdleTimer)
  scrollIdleTimer = setTimeout(() => { scrollIdleTimer = null }, 160)
  if (scrollFrame) return
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = null
    updateScrollRow()
  })
}
function resetScroll() {
  closeHover()
  clearScrollActivity()
  if (gridRef.value) gridRef.value.scrollTop = 0
  scrollRow.value = 0
  nextTick(measureGrid)
}
watch(gridRef, el => {
  resizeObserver?.disconnect()
  if (!el) return
  resizeObserver = new ResizeObserver(measureGrid)
  resizeObserver.observe(el)
  measureGrid()
})
onMounted(() => {
  document.addEventListener('pointermove', checkPointer, { passive: true })
  document.addEventListener('mouseout', leaveWindow)
  window.addEventListener('blur', closeHover)
  window.addEventListener('resize', closeHover)
})
onBeforeUnmount(() => {
  resizeObserver?.disconnect(); closeHover(); clearTimeout(searchTimer); clearScrollActivity()
  document.removeEventListener('pointermove', checkPointer)
  document.removeEventListener('mouseout', leaveWindow)
  window.removeEventListener('blur', closeHover)
  window.removeEventListener('resize', closeHover)
})
const selectionReason = item => assetSelectionReason(item, { selected: selectedItems.value, limits: props.limits, isWan: props.isWan })
const selectionError = computed(() => selectedItems.value.map(selectionReason).find(Boolean) || '')
function resetSelection() { selectedKeys.value = [...new Set(props.items.filter(item => item.inQueue).sort((a, b) => (a.queueOrder ?? 0) - (b.queueOrder ?? 0)).map(assetSelectionKey))]; closeHover(); previewItem.value = null; clearTimeout(searchTimer); searchTerm.value = ''; keyword.value = ''; activeType.value = 'all'; resetScroll() }
function toggleItem(item) {
  const key = assetSelectionKey(item)
  if (selectedKeys.value.includes(key)) selectedKeys.value = selectedKeys.value.filter(value => value !== key)
  else if (!selectionReason(item)) selectedKeys.value.push(key)
}
watch(keyword, value => {
  closeHover(); clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { searchTerm.value = value.trim().replace(/^@/, '').toLowerCase(); resetScroll() }, 120)
})
watch(activeType, resetScroll)
watch(filteredItems, () => nextTick(measureGrid))
watch(itemMap, items => { selectedKeys.value = selectedKeys.value.filter(key => items.has(key)) })
watch(() => props.modelValue, open => { if (!open) { closeHover(); clearScrollActivity(); previewItem.value = null; uploadVisible.value = false; clearTimeout(searchTimer) } })
</script>

<style scoped>
:global(.video-library-delete-confirm) { background: #292929; border-color: #4b4143; --el-text-color-primary: #eee; --el-text-color-regular: #ccc; --el-color-primary: #bd858d; --el-color-primary-light-3: #d6a5ac; --el-fill-color-blank: #333; --el-border-color: #555; color-scheme: dark; }
:global(.video-asset-library.el-dialog) { display: flex; flex-direction: column; height: 88vh; max-width: calc(100vw - 48px); margin-bottom: 0; padding: 24px; border: 1px solid #454545; border-radius: 18px; background: #212121; color: #ddd; color-scheme: dark; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft YaHei', sans-serif; --el-text-color-primary: #eee; --el-text-color-regular: #ccc; --el-text-color-placeholder: #888; --el-bg-color: #212121; --el-fill-color-blank: #292929; --el-fill-color-light: #383838; --el-color-primary: #ddd; --el-border-color: #484848; --el-mask-color: rgb(20 20 20 / 65%); }
:global(.video-asset-library .el-dialog__title) { font-size: 18px; font-weight: 600; }
:global(.video-asset-library .el-dialog__header) { flex-shrink: 0; margin: 0; padding: 0 36px 20px 0; }
:global(.video-asset-library .el-dialog__body) { flex: 1; min-height: 0; padding: 0; }
:global(.video-asset-library .el-dialog__footer) { flex-shrink: 0; margin-top: 16px; padding: 16px 0 0; border-top: 1px solid #383838; }
.library-layout { position: relative; display: grid; grid-template-columns: minmax(0, 1fr) 280px; gap: 24px; height: 100%; min-height: 0; }
.library-browser-heading { display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; height: 30px; margin-bottom: 12px; color: #aaa; font-size: 13px; }
.library-upload-trigger { display: flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid #484848; border-radius: 8px; background: #303030; color: #ddd; font: inherit; font-size: 12px; cursor: pointer; }
.library-upload-trigger:hover:not(:disabled) { background: #383838; border-color: #777; }
.library-upload-trigger:disabled { opacity: .4; cursor: default; }
.library-browser { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.library-toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding-bottom: 16px; }
.library-search { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 180px; padding: 10px 12px; border: 1px solid #484848; border-radius: 9px; color: #888; background: #292929; }
.library-search:focus-within { border-color: #888; }
.library-search input { width: 100%; min-width: 0; padding: 0; border: none; outline: none; background: transparent; color: #eee; font: inherit; font-size: 13px; }
.library-type-filters { display: flex; gap: 4px; }
.library-type-filters button { display: flex; align-items: center; gap: 6px; padding: 8px 10px; border: 1px solid transparent; border-radius: 8px; background: transparent; color: #999; cursor: pointer; font: inherit; font-size: 12px; }
.library-type-filters button:hover { color: #eee; background: #303030; }
.library-type-filters button.active { color: #eee; background: #383838; border-color: #555; }
.library-type-filters button span { font-size: 10px; color: #999; font-variant-numeric: tabular-nums; }
.library-grid-scroll { --library-columns: 6; --library-gap: 12; flex: 1; min-height: 0; overflow-y: auto; overflow-anchor: none; padding: 3px; }
.library-scroll { scrollbar-width: thin; scrollbar-color: #555 transparent; scrollbar-gutter: stable; overscroll-behavior: contain; }
.library-scroll::-webkit-scrollbar { width: 7px; height: 7px; }
.library-scroll::-webkit-scrollbar-track { background: transparent; }
.library-scroll::-webkit-scrollbar-thumb { border: 2px solid transparent; border-radius: 8px; background: #555; background-clip: padding-box; }
.library-scroll::-webkit-scrollbar-thumb:hover { background: #777; background-clip: padding-box; }
.library-grid-space { position: relative; }
.library-asset-grid { position: absolute; left: 0; right: 0; display: grid; grid-template-columns: repeat(var(--library-columns), minmax(0, 1fr)); align-content: start; gap: calc(var(--library-gap) * 1px); }
.library-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; height: 100%; color: #888; font-size: 12px; text-align: center; }
.library-empty .el-icon { font-size: 32px; color: #777; }
.library-empty strong { color: #ccc; font-size: 14px; font-weight: 500; }
.library-results { flex-shrink: 0; padding-top: 12px; color: #888; font-size: 12px; }
.library-selection { display: flex; flex-direction: column; min-width: 0; min-height: 0; padding: 16px; border: 1px solid #3a3a3a; border-radius: 14px; background: #262626; }
.library-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-shrink: 0; }
.library-section-heading strong { color: #ddd; font-size: 14px; font-weight: 500; }
.library-section-heading strong span { margin-left: 6px; color: #999; font-variant-numeric: tabular-nums; }
.library-text-button { padding: 0; border: none; background: none; color: #ccc; font: inherit; font-size: 12px; cursor: pointer; }
.library-text-button:hover:not(:disabled) { color: #fff; }
.library-text-button:disabled { color: #666; cursor: default; }
.library-limits { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; flex-shrink: 0; margin: 14px 0; padding-bottom: 14px; border-bottom: 1px solid #3a3a3a; font-size: 11px; color: #999; }
.library-limits > span { display: flex; align-items: center; gap: 4px; white-space: nowrap; }
.library-limits b { color: #ddd; font-weight: 400; font-style: normal; font-variant-numeric: tabular-nums; }
.library-selected-scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 3px; }
.library-selected-strip { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-content: start; gap: 10px; }
.library-selection-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; height: 100%; color: #888; text-align: center; font-size: 12px; }
.library-selection-empty .el-icon { font-size: 28px; color: #666; }
.library-selection-empty small { font-size: 11px; color: #777; }
.library-selection-note { flex-shrink: 0; margin: 14px 0 0; padding-top: 12px; border-top: 1px solid #3a3a3a; color: #888; font-size: 11px; line-height: 1.6; }
.library-load-error { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 10px; color: #e6a4a4; font-size: 12px; }
.library-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.library-footer > span { text-align: left; color: #999; font-size: 12px; }
.library-footer > div { display: flex; flex-shrink: 0; }
.library-footer :deep(.el-button) { height: 38px; border-radius: 10px; }
.library-confirm.el-button { min-width: 110px; background: #e3e3e3; border-color: #e3e3e3; color: #171717; font-weight: 600; }
.library-confirm.el-button:hover:not(.is-disabled) { background: #fff; border-color: #fff; }
.library-confirm.el-button.is-disabled { background: #444; border-color: #444; color: #888; }
@media (max-width: 1100px) {
  .library-layout { grid-template-columns: minmax(0, 1fr) 240px; gap: 16px; }
  .library-grid-scroll { --library-columns: 4; --library-gap: 10; }
  .library-search { flex-basis: 100%; }
}
@media (max-width: 900px) {
  .library-layout { grid-template-columns: minmax(0, 1fr) 200px; }
}
@media (max-width: 760px) {
  :global(.video-asset-library.el-dialog) { height: 94dvh; max-width: calc(100vw - 20px); margin-top: 3dvh; padding: 14px; }
  .library-layout { grid-template-columns: minmax(0, 1fr) 160px; gap: 12px; }
  .library-grid-scroll { --library-columns: 3; --library-gap: 8; }
  .library-selection { padding: 10px; border-radius: 10px; }
  .library-limits { justify-content: flex-start; gap: 8px 12px; }
  .library-selected-strip { grid-template-columns: 1fr; }
  .library-toolbar { gap: 10px; padding-bottom: 12px; }
  .library-type-filters { flex-wrap: wrap; }
  .library-type-filters button { padding: 6px; }
  .library-footer > span { max-width: 42%; }
}
@media (max-width: 520px) {
  .library-layout { grid-template-columns: minmax(0, 1fr) 112px; gap: 10px; }
  .library-grid-scroll { --library-columns: 2; --library-gap: 6; }
  .library-search { min-width: 0; padding: 8px; }
  .library-search input { font-size: 12px; }
  .library-selection { padding: 8px; }
  .library-section-heading { flex-wrap: wrap; }
  .library-section-heading strong { font-size: 12px; }
  .library-limits { margin: 12px 0; }
  .library-footer > span { display: none; }
  .library-footer > div { margin-left: auto; }
}
</style>
